import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import Accordion from "@mui/material/Accordion"
import AccordionActions from "@mui/material/AccordionActions"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import Grid from "@mui/material/Grid"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useConfirm } from "material-ui-confirm"
import React, { useMemo, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { serviceDelete, serviceQuestionInsert, serviceUpdate } from "../store/actions"
import {
	selectQuestionsByServiceIdAndParentId,
	selectQuestionsWithOnlyIdsByServiceId,
} from "../store/reducers/serviceQuestionReducer"
import { selectServiceById } from "../store/reducers/serviceReducer"
import RecursiveQuestionsAdmin from "./recursiveQuestionsAdmin"

const Service = ({ serviceId, styles, clipboard, setClipboard }) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const dispatch = useDispatch()
	const confirm = useConfirm()

	const service = useSelector((state) => {
		return selectServiceById(state, serviceId)
	}, shallowEqual)
	const sObj = useMemo(() => {
		return service
	}, [service])

	const servicequestions = useSelector((state) => {
		return selectQuestionsWithOnlyIdsByServiceId(state, serviceId)
	}, shallowEqual)
	const servicequestionsMemo = useMemo(() => servicequestions, [servicequestions])

	const servicequestionsOrdered = useSelector((state) => {
		return selectQuestionsByServiceIdAndParentId(state, serviceId, null)
	}, shallowEqual)
	const servicequestionsOrderedMemo = useMemo(() => servicequestionsOrdered, [servicequestionsOrdered])

	const sDelete = (id) => dispatch(serviceDelete(id))
	const sqInsert = (servicequestion, sid, sqid, sqorder) =>
		dispatch(serviceQuestionInsert(servicequestion, sid, sqid, sqorder))
	const sUpdate = (json, id) => dispatch(serviceUpdate(json, id))

	const showHideDetail = () => {
		setIsExpanded(!isExpanded)
	}

	const pasteQuestion = async (sqid, sqorder) => {
		// Lisää uusi kysymys kantaan
		let newSqorderId = 0
		if (!sqid) {
			// find highest sqorder id and add 1 to it
			newSqorderId =
				servicequestionsOrderedMemo.reduce((max, obj) => {
					return Math.max(max, obj.sqorder)
				}, 0) + 1
		} else {
			newSqorderId = sqorder
		}
		const newQuestion = {
			...clipboard,
			serviceid: serviceId,
			servicequestionid: null,
			parent: sqid || null,
		}
		let response
		try {
			response = await sqInsert(newQuestion, serviceId, null, newSqorderId)
		} catch (e) {
			console.log("ERROR:", e)
		}

		const recursiveInsertQuestion = async (question, parentId) => {
			for (let child of question.childquestions) {
				let newChild = {
					...child,
					serviceid: serviceId,
					servicequestionid: null,
					parent: parentId,
				}
				let res
				try {
					res = await sqInsert(newChild, serviceId, child.servicequestionid, child.sqorder)
				} catch (e) {
					console.log("ERROR:", e)
				}
				await recursiveInsertQuestion(child, res[0].servicequestionid)
			}
		}

		if (newQuestion.childquestions.length > 0) {
			await recursiveInsertQuestion(newQuestion, response[0].servicequestionid)
		}
	}

	return (
		<Accordion sx={styles.root} expanded={isExpanded} TransitionProps={{ unmountOnExit: true }}>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				onClick={showHideDetail}
				aria-controls={"panel" + serviceId + "a-content"}
				id={"panel" + serviceId}>
				<Typography variant="h6">
					{sObj.title} - {sObj.description}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				{isExpanded && (
					<Grid container direction="column">
						<Grid item sx={styles.text}>
							<Typography component={"span"} variant="body1" sx={{ display: "flex", flexDirection: "column" }}>
								<TextField
									label="Otsikko"
									InputLabelProps={{ shrink: true }}
									name={"title"}
									variant="outlined"
									id={"title" + serviceId}
									onBlur={(e) => sUpdate({ title: e.target.value }, serviceId)}
									defaultValue={sObj.title !== null ? sObj.title : ""}></TextField>

								<TextField
									label="Kuvaus"
									InputLabelProps={{ shrink: true }}
									name={"description"}
									variant="outlined"
									id={"description" + serviceId}
									onBlur={(e) => sUpdate({ description: e.target.value }, serviceId)}
									defaultValue={sObj.description !== null ? sObj.description : ""}></TextField>

								<TextField
									label="Tarkka Kuvaus"
									InputLabelProps={{ shrink: true }}
									name={"longdescription"}
									variant="outlined"
									id={"longdescription" + serviceId}
									onBlur={(e) => sUpdate({ longdescription: e.target.value }, serviceId)}
									defaultValue={sObj.longdescription !== null ? sObj.longdescription : ""}></TextField>

								<TextField
									label="Yksikköhinta"
									variant="outlined"
									InputLabelProps={{ shrink: true }}
									type="number"
									name={"costperunit"}
									id={"costperunit" + serviceId}
									onBlur={(e) => sUpdate({ costperunit: e.target.value }, serviceId)}
									defaultValue={sObj.costperunit !== null ? sObj.costperunit : ""}></TextField>
							</Typography>
						</Grid>
						<Divider />
						<Grid item sx={{ display: "inline-block" }}>
							<List dense sx={{ gap: 1, display: "flex", flexDirection: "column" }}>
								{servicequestionsMemo.map((sqObj, index) => {
									return (
										<RecursiveQuestionsAdmin
											key={sqObj}
											questionId={sqObj}
											serviceId={serviceId}
											styles={styles}
											setClipboard={setClipboard}
											clipboard={clipboard}
											pasteQuestion={pasteQuestion}
										/>
									)
								})}
								<ListItem>
									<Button
										variant="outlined"
										color="primary"
										onClick={() => {
											sqInsert(
												null,
												serviceId,
												null,
												servicequestionsOrderedMemo.reduce((max, obj) => {
													console.log("max:", max, "obj:", obj)
													return Math.max(max, parseInt(obj.sqorder))
												}, 0) + 1
											)
										}}>
										Lisää Kysymys
									</Button>
									<Button variant="outlined" color="primary" onClick={() => pasteQuestion()} disabled={!clipboard}>
										Liitä kysymys
									</Button>
								</ListItem>
							</List>
						</Grid>
					</Grid>
				)}
			</AccordionDetails>
			<Divider />
			<AccordionActions>
				<Button
					size="small"
					onClick={() => {
						confirm({
							title: "Haluatko poistaa palvelun?",
							description:
								"Tämä poistaa palvelun ja kaikki siihen liittyvät kysymykset. Toimintoa ei voi peruuttaa tietokanta tallennuksen jälkeen.",
							confirmationText: "Kyllä",
							cancellationText: "Peruuta",
						})
							.then(() => {
								sDelete(sObj.serviceid)
							})
							.catch((err) => {
								console.log("Error:", err)
							})
					}}>
					Poista
				</Button>
				<Button size="small" onClick={() => setIsExpanded(false)}>
					Sulje
				</Button>
			</AccordionActions>
		</Accordion>
	)
}

export default React.memo(Service)
